import {
  AppBar,
  Box,
  Drawer,
  IconButton,
  Stack,
  Toolbar,
  Typography,
  useMediaQuery,
  type Theme,
} from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import MenuIcon from '@mui/icons-material/Menu';
import SearchIcon from '@mui/icons-material/Search';
import { Tooltip } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { Link as RouterLink, Outlet } from 'react-router-dom';

import * as authApi from '@/api/auth';
import { getTokenExpiresAt, setToken, subscribeTokenChange } from '@/api/tokenStorage';
import { CommandPalette } from '@/components/CommandPalette/CommandPalette';
import { PermissionGate } from '@/components/PermissionGate';
import { DuplicateRunsHost } from '@/features/activities/record/actions/DuplicateRunsHost';
import { BgTaskBell } from '@/features/bgtasks/BgTaskBell';
import { DebugToolbarButton } from '@/features/debug/DebugToolbarButton';
import { ScopeSelector } from '@/scope/ScopeSelector';
import { TenantScopeUrlSync } from '@/scope/TenantScopeUrlSync';
import { useScopeAwareAnchors } from '@/scope/useScopeAwareAnchors';

import { AppScroller } from './AppScroller';
import { BrandLogo } from './BrandLogo';
import { NAV_DRAWER_PAPER_SX, SidebarMenu } from './SidebarMenu';
import { UserMenu } from './UserMenu';

const DRAWER_WIDTH = 240;
const RAIL_WIDTH = 64;
/** localStorage key for the desktop drawer state: 'open' | 'rail'. */
const DRAWER_STATE_KEY = 'console.drawer';

/** The docked drawer animates between full width and the icon rail — except
 *  under reduced motion, where it snaps (DESIGN.md: honour
 *  `prefers-reduced-motion` on every transition). Spread into both the drawer
 *  and its paper, which animate together. */
export const RAIL_WIDTH_TRANSITION_SX = {
  transition: (t: Theme) => t.transitions.create('width'),
  '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
};

export function AppShell() {
  const isMobile = useMediaQuery((t: Theme) => t.breakpoints.down('md'));
  // Desktop: full drawer vs icon rail (persisted). Mobile: overlay drawer.
  const [drawerOpen, setDrawerOpen] = useState(
    () => localStorage.getItem(DRAWER_STATE_KEY) !== 'rail',
  );
  const [mobileOpen, setMobileOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  useTokenRotation();
  // Internal links get the console scope stamped into their href, so ⌘-click
  // and "copy link address" carry it into a new tab.
  useScopeAwareAnchors();

  const toggleDrawer = () => {
    if (isMobile) {
      setMobileOpen((v) => !v);
      return;
    }
    const next = !drawerOpen;
    setDrawerOpen(next);
    localStorage.setItem(DRAWER_STATE_KEY, next ? 'open' : 'rail');
  };
  const expandDrawer = () => {
    setDrawerOpen(true);
    localStorage.setItem(DRAWER_STATE_KEY, 'open');
  };

  return (
    <Box sx={{ display: 'flex', height: '100dvh' }}>
      <AppBar position="fixed" sx={{ zIndex: (t) => t.zIndex.drawer + 1 }}>
        <AppBarRow onToggleNav={toggleDrawer} onSearch={() => setPaletteOpen(true)} />
      </AppBar>

      <TenantScopeUrlSync />
      {/* Activity copies outlive their page: their end is announced from here. */}
      <DuplicateRunsHost />

      <CommandPalette
        open={paletteOpen}
        onOpen={() => setPaletteOpen(true)}
        onClose={() => setPaletteOpen(false)}
      />

      {isMobile ? (
        // Overlay drawer (renders in a modal, above the AppBar); leaf
        // navigation closes it.
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            [`& .MuiDrawer-paper`]: {
              width: DRAWER_WIDTH,
              boxSizing: 'border-box',
              ...NAV_DRAWER_PAPER_SX,
            },
          }}
        >
          <SidebarMenu onNavigate={() => setMobileOpen(false)} />
        </Drawer>
      ) : (
        // Docked drawer: full width or icon rail — never fully hidden, so
        // navigation stays one click away. Width transitions (none under
        // reduced motion); the sibling main area reflows via flexGrow.
        <Drawer
          variant="permanent"
          sx={{
            width: drawerOpen ? DRAWER_WIDTH : RAIL_WIDTH,
            flexShrink: 0,
            whiteSpace: 'nowrap',
            ...RAIL_WIDTH_TRANSITION_SX,
            [`& .MuiDrawer-paper`]: {
              width: drawerOpen ? DRAWER_WIDTH : RAIL_WIDTH,
              overflowX: 'hidden',
              boxSizing: 'border-box',
              ...RAIL_WIDTH_TRANSITION_SX,
              ...NAV_DRAWER_PAPER_SX,
            },
          }}
        >
          <Toolbar variant="dense" />
          <SidebarMenu mini={!drawerOpen} onExpandGroup={expandDrawer} />
        </Drawer>
      )}

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          minWidth: 0, // prevent flex child from overflowing its container
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <Toolbar variant="dense" /> {/* spacer matching the fixed AppBar height */}
        {/* The app's scroll container (the document itself never scrolls). */}
        <AppScroller>
          <Outlet />
        </AppScroller>
      </Box>
    </Box>
  );
}

/** The app bar's one row. Below `sm` (a 390 px phone) it keeps to the viewport: the logo
 *  without the "Ops Console" words, the scope as an icon (the tenant in its name and
 *  tooltip), tighter gaps, and Help moved into the user menu — every control stays one tap
 *  away, nothing lays out past the edge. */
export function AppBarRow({ onToggleNav, onSearch }: { onToggleNav: () => void; onSearch: () => void }) {
  const narrow = useMediaQuery((t: Theme) => t.breakpoints.down('sm'));
  return (
    <Toolbar variant="dense" sx={{ gap: narrow ? 1 : 2 }} data-app-bar-row={narrow ? 'narrow' : 'wide'}>
      <IconButton color="inherit" edge="start" aria-label="Toggle navigation" onClick={onToggleNav}>
        <MenuIcon />
      </IconButton>
      <Box
        component={RouterLink}
        to="/"
        sx={{
          display: 'flex',
          alignItems: 'center',
          minWidth: 0,
          color: 'inherit',
          textDecoration: 'none',
          '&:hover': { opacity: 0.85 },
        }}
      >
        <BrandLogo />
        {!narrow && (
          <Typography variant="h6" noWrap sx={{ ml: 1 }}>
            Ops Console
          </Typography>
        )}
      </Box>
      <Box sx={{ flexGrow: 1 }} />
      <Stack direction="row" alignItems="center" spacing={narrow ? 0.5 : 2} sx={{ flex: 'none' }}>
        <ScopeSelector compact={narrow} />
        <Tooltip title="Search everything (⌘K)">
          <IconButton color="inherit" aria-label="Search everything" onClick={onSearch}>
            <SearchIcon />
          </IconButton>
        </Tooltip>
        {!narrow && (
          <Tooltip title="Help">
            <IconButton color="inherit" aria-label="Help" component={RouterLink} to="/help">
              <HelpOutlineIcon />
            </IconButton>
          </Tooltip>
        )}
        <BgTaskBell />
        <PermissionGate perm="console_debug">
          <DebugToolbarButton />
        </PermissionGate>
        <UserMenu withHelp={narrow} />
      </Stack>
    </Toolbar>
  );
}

/** Rotate the bearer token 90 s before it expires.
 *
 * The session is SHARED across tabs (tokenStorage BroadcastChannel), and the
 * backend deletes the old token on rotate — so every tab runs this hook
 * against the same token. Two guards keep them from racing each other:
 * a small random jitter de-synchronizes the otherwise identical timers, and
 * the fire-time expiry check skips the rotation when another tab already
 * rotated (the adopted token re-arms the timer via subscribeTokenChange). */
function useTokenRotation() {
  const timer = useRef<number | null>(null);
  useEffect(() => {
    function schedule() {
      if (timer.current) window.clearTimeout(timer.current);
      const expires = getTokenExpiresAt();
      if (!expires) return;
      const jitter = Math.random() * 15_000;
      const ms = new Date(expires).getTime() - Date.now() - 90_000 + jitter;
      if (ms <= 0) return;
      timer.current = window.setTimeout(async () => {
        if (getTokenExpiresAt() !== expires) {
          // Another tab rotated while we waited — re-arm against the new token.
          schedule();
          return;
        }
        try {
          const r = await authApi.rotateToken();
          setToken(r.token, r.token_expires);
        } catch {
          // 401 from the rotate request will be handled by the interceptor.
        } finally {
          schedule();
        }
      }, ms);
    }
    schedule();
    const unsubscribe = subscribeTokenChange(() => schedule());
    return () => {
      unsubscribe();
      if (timer.current) window.clearTimeout(timer.current);
    };
  }, []);
}
